<template>
  <lf-modal :title="$t('DEALS.DEAD.TITLE')" :close="close">
    <template #content>
      <div data-cy="dead-modal" class="space-y-5 p-6">
        <lf-dropdown
          name="reason"
          :value="reason || undefined"
          :placeholder="$t('DEALS.DEAD.REASON_PLACEHOLDER')"
          :options="reasons"
          full-width
          data-cy="dead-statuses"
          @change="onReasonChange"
        />
        <lf-dropdown
          v-if="showSubReason"
          name="sub_reason"
          :value="subReason || undefined"
          :placeholder="$t('DEALS.DEAD.SUB_REASON_PLACEHOLDER')"
          :options="subReasons"
          full-width
          data-cy="dead-substatuses"
          @change="onSubReasonChange"
        />
        <lf-textarea
          v-model="notes"
          name="notes"
          class="h-40 w-full resize-none rounded-sm border-gray-200 placeholder-gray-300"
          :placeholder="$t('DEALS.DEAD.DESCRIPTION_PLACEHOLDER')"
          :character-limit="TEXT_CHARACTER_LIMIT_MEDIUM"
          rows="4"
          noresize
        />
        <lf-checkbox
          v-if="isLendflowUser"
          v-model="sendEmail"
          v-tooltip="{
            content: $t('DEALS.DEAD.EMAIL_NOTIFICATION_WARNING'),
            onShow: () => !hasSetDeadStageEmailNotification,
          }"
          value=""
          name="sendEmail"
          class="w-fit-content"
          :disabled="!hasSetDeadStageEmailNotification"
        >
          {{ $t("DEALS.DEAD.SEND_EMAIL") }}
        </lf-checkbox>
      </div>
      <modal-buttons
        class="justify-end border-t border-gray-200 bg-white py-5"
        :on-cancel="close"
        :on-save="decline"
        :disabled="!reason || isDeclining || dealIsDead"
      />
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";

import { AnalyticsEvent } from "@/enums/analytics";
import { STATUS_DEAD, TEXT_CHARACTER_LIMIT_MEDIUM } from "@/helpers/constants";
import { useAuth } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import { useDeals } from "@/hooks/deals";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";
import { useAnalyticsStore } from "@/stores/analytics";

import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfTextarea from "@/components/ui/inputs/LfTextarea.vue";

import type { IRootState } from "@/models/state";
import type { IWorkflowStatus } from "@/models/workflows";
import type { PropType } from "vue";

const props = defineProps({
  close: {
    type: Function as PropType<() => void>,
    required: true,
  },
});

const { getters, commit, dispatch } = useStore<IRootState>();
const { isLendflowUser } = useAuth();
const { activeTemplateEmailNotifications } = useActiveWorkflowTemplate();
const { activeDeal } = useDeals();
const { t } = useI18n();
const { trackEvent } = useAnalyticsStore();

const reason = ref<string | number | null>(null);
const notes = ref("");
const sendEmail = ref(false);
const subReason = ref<string | number | null>(null);

const dealIsDead = computed(() => activeDeal.value?.status === STATUS_DEAD);

const statuses = computed<IWorkflowStatus[]>(() =>
  getters["applications/workflowStatusesByDealId"](activeDeal.value?.id)
);

const stageStatuses = computed(
  () => statuses.value?.filter(({ live }) => !live) ?? []
);

const reasons = computed(
  () => stageStatuses.value?.map((status) => status.name) ?? []
);

const hasSetDeadStageEmailNotification = computed(() => {
  const deadEmailSettings = activeTemplateEmailNotifications.value?.filter(
    ({ stage }) => stage === "dead"
  );

  return deadEmailSettings?.some(({ enabled }) => enabled);
});

const subReasons = computed(() => {
  if (!reason.value) {
    return [];
  }
  const stageStatus = stageStatuses.value?.find(
    (status) => status.name === reason.value
  );
  return stageStatus?.substatuses || [];
});

const showSubReason = computed(() => reason.value && subReasons.value?.length);

const onReasonChange = (reasonId: string | number) => {
  reason.value = reasons.value[Number(reasonId)];
  subReason.value = null;
};

const onSubReasonChange = (subReasonId: string | number) => {
  subReason.value = subReasons.value[Number(subReasonId)];
};

const declineCallback = async () => {
  if (!reason.value) {
    return;
  }

  await dispatch("applications/killActiveDeal", {
    reason: reason.value,
    sub_reason: subReason.value,
    notes: notes.value,
    send_email: sendEmail.value,
  });

  trackEvent(AnalyticsEvent.WflDealKilled, {
    applicationId: activeDeal.value.id,
  });

  commit("setGlobalMessage", {
    title: t("DEALS.DEAD.DONE"),
    type: "success",
  });
};

const { fetchWrapper: decline, loading: isDeclining } = usePromiseWrapper(
  declineCallback,
  {
    finally: props.close,
  }
);

const { fetchWrapper: getStatuses } = usePromiseWrapper(async () => {
  if (isEmpty(statuses.value)) {
    await dispatch("applications/getWorkflowStatuses", activeDeal.value?.id);
  }
});

onMounted(() => {
  getStatuses();
});
</script>
